/**
 * Combat screen — the core fight UI.
 *
 *  - Enemies (ICE) with telegraphed intents, HP bars, block, status pips.
 *  - The Trace meter (signature tension mechanic) with hard-countdown readout.
 *  - Runner panel: avatar, HP, block, statuses, Cycles (energy).
 *  - Hand dock: playable cards, cost affordability, targeting flow.
 *  - End Turn + Deck/piles peek.
 *  - Floating damage/block/heal numbers driven by the last engine events.
 *
 * All rules live in the engine; this only reads state + dispatches reducers via
 * the store. Targeting is a two-step UI flow (select card -> click ICE) that
 * resolves to store.playCard(handIndex, targetUid).
 */

import { el, div, button } from "../dom";
import {
  topbar,
  hpBar,
  traceMeter,
  statusPips,
  cardFace,
} from "../widgets";
import {
  intentText,
  intentGlyph,
  intentClass,
  iceGlyph,
} from "../glyphs";
import { getCardDef, getIceDef, getRunnerBlock } from "../../engine";
import type { Store } from "../store";
import type {
  CardInstance,
  IceInstance,
  RunState,
  CombatEvent,
} from "../../engine/types";

/** UI selection state for the targeting flow (module-local; reset per action). */
interface CombatUi {
  selectedHandIndex: number | null;
  /** Keyboard focus index in the hand (-1 = none). */
  focusHand: number;
}
const ui: CombatUi = { selectedHandIndex: null, focusHand: -1 };

export function resetCombatUi(): void {
  ui.selectedHandIndex = null;
  ui.focusHand = -1;
}

export function renderCombat(store: Store): HTMLElement {
  const run = store.run!;
  const combat = run.combat!;
  const trace = run.trace!;
  const runnerBlock = getRunnerBlock(run);

  // Clamp stale selection (hand may have shrunk).
  if (
    ui.selectedHandIndex != null &&
    ui.selectedHandIndex >= combat.hand.length
  ) {
    ui.selectedHandIndex = null;
  }

  const selectedCard =
    ui.selectedHandIndex != null ? combat.hand[ui.selectedHandIndex] : undefined;
  const targeting =
    selectedCard != null &&
    getCardDef(selectedCard.defId).target === "enemy";

  const screen = div("screen");
  screen.appendChild(topbar(run, () => store.open("deck")));

  const body = div("screen__body");

  // --- Trace meter (top, full width) -------------------------------------
  body.appendChild(
    traceMeter(trace.value, trace.max, trace.hardCountdown),
  );

  // --- Enemies row -------------------------------------------------------
  const enemiesWrap = div("enemies");
  enemiesWrap.style.flex = "1 1 auto";
  enemiesWrap.style.alignItems = "center";
  for (const ice of combat.enemies) {
    enemiesWrap.appendChild(
      iceCard(store, ice, targeting, () => {
        if (ui.selectedHandIndex != null && ice.alive) {
          const idx = ui.selectedHandIndex;
          ui.selectedHandIndex = null;
          store.playCard(idx, ice.uid);
        }
      }),
    );
  }
  body.appendChild(enemiesWrap);

  // --- Runner + control row ----------------------------------------------
  const controlRow = div("row");
  controlRow.style.alignItems = "stretch";
  controlRow.appendChild(runnerPanel(run, runnerBlock));
  controlRow.appendChild(div("fill")); // spacer
  controlRow.appendChild(combatControls(store, run));
  body.appendChild(controlRow);

  // --- Hand --------------------------------------------------------------
  body.appendChild(handDock(store, combat, run, targeting));

  screen.appendChild(body);

  // --- Key hints ---------------------------------------------------------
  screen.appendChild(
    div("keyhints", [
      hint("1-9/0", "play card"),
      hint("←→", "focus card"),
      hint("Enter", "play focused"),
      hint("Tab", "next target"),
      hint("E", "end turn"),
      hint("V", "view deck"),
      hint("Esc", "cancel/menu"),
    ]),
  );

  // --- Floating numbers (driven by last events) --------------------------
  queueFloaters(screen, store.lastEvents);

  return screen;
}

function hint(k: string, label: string): HTMLElement {
  return el("span", {}, [el("kbd", { text: k }), ` ${label}`]);
}

/** A single ICE display: intent band, sprite, name, HP, block, statuses. */
function iceCard(
  _store: Store,
  ice: IceInstance,
  targeting: boolean,
  onTarget: () => void,
): HTMLElement {
  const def = getIceDef(ice.defId);
  let cls = "ice";
  if (def.isElite) cls += " elite";
  if (def.isBoss) cls += " boss";
  if (!ice.alive) cls += " dead";
  else if (targeting) cls += " targetable targeting-on";

  const node = div(cls);
  node.setAttribute("data-uid", String(ice.uid));

  if (ice.alive) {
    node.appendChild(
      div(`ice__intent ${intentClass(ice.intent)}`, [
        `${intentGlyph(ice.intent)} ${intentText(ice.intent)}`,
      ]),
    );
  } else {
    node.appendChild(div("ice__intent", ["— DOWN —"]));
  }

  node.appendChild(div("ice__sprite", [iceGlyph(ice.defId)]));
  node.appendChild(div("ice__name", [def.name]));
  node.appendChild(div("ice__hp", [hpBar(ice.hp, ice.maxHp, 138)]));
  node.appendChild(
    div("ice__pips", [statusPips(ice.statuses)]),
  );
  if (ice.block > 0) {
    node.appendChild(div("ice__block", [`▣${ice.block}`]));
  }

  if (ice.alive && targeting) {
    node.addEventListener("click", onTarget);
  }
  return node;
}

/** The runner status panel (left of controls). */
function runnerPanel(run: RunState, block: number): HTMLElement {
  const charName = run.character === "ghost" ? "GHOST" : "BREAKER";
  const avatarGlyph = run.character === "ghost" ? "ᗧ" : "ᗣ";
  return div("runner", [
    div("runner__avatar", [avatarGlyph]),
    div("col", [
      el("div", { class: "h-sub", text: charName, style: "font-size:13px" }),
      hpBar(run.hp, run.maxHp, 180),
      statusPips(run._runnerStatuses, block),
    ]),
  ]);
}

/** Cycles (energy) display + End Turn + pile counts. */
function combatControls(store: Store, run: RunState): HTMLElement {
  const combat = run.combat!;
  const totalCyc = combat.cyclesPerTurn;
  const cycRow = div("cycles");
  // Render up to 8 cycle pips; lit ones reflect currently-available Cycles.
  for (let i = 0; i < Math.max(totalCyc, combat.cycles, 1) && i < 8; i++) {
    cycRow.appendChild(div(`cyc${i < combat.cycles ? " on" : ""}`));
  }
  const cycLabel = el("div", {
    class: "h-sub",
    text: `${combat.cycles}`,
    style: "color:var(--amber);font-size:20px",
  });

  const endBtn = button(
    "btn btn--primary",
    "END TURN ▶",
    () => {
      if (store.settings.confirmEndTurn && combat.cycles > 0) {
        if (!confirm("You still have Cycles. End turn anyway?")) return;
      }
      resetCombatUi();
      store.endTurn();
    },
    { disabled: combat.phase !== "player" },
  );

  return div("col", [
    div("panel", [
      el("div", { class: "muted", text: "CYCLES", style: "font-size:10px" }),
      div("row", [cycLabel, cycRow]),
    ]),
    div("piles", [
      el("span", {}, [el("b", { text: String(combat.drawPile.length) }), " draw"]),
      el("span", {}, [
        el("b", { text: String(combat.discardPile.length) }),
        " disc",
      ]),
      el("span", {}, [
        el("b", { text: String(combat.exhaustPile.length) }),
        " exh",
      ]),
    ]),
    div("row", [
      button("btn btn--sm", "DECK", () => store.open("deck")),
      endBtn,
    ]),
  ]);
}

/** The hand of cards. */
function handDock(
  store: Store,
  combat: RunState["combat"],
  run: RunState,
  targeting: boolean,
): HTMLElement {
  const c = combat!;
  const dock = div("hand");
  if (c.hand.length === 0) {
    dock.appendChild(div("hand-empty", ["— empty hand —"]));
    return dock;
  }
  c.hand.forEach((card: CardInstance, i: number) => {
    const def = getCardDef(card.defId);
    const affordable = def.cost <= c.cycles && !def.unplayable;
    const selected = ui.selectedHandIndex === i;
    const face = cardFace(card.defId, {
      affordable,
      playable: !def.unplayable,
      selected,
      focused: ui.focusHand === i,
      onClick: () => onCardClick(store, run, i),
    });
    dock.appendChild(face);
  });
  if (targeting) {
    // Hint banner shows we're awaiting a target.
    dock.style.opacity = "1";
  }
  return dock;
}

/** Click a card: play immediately if no target needed; else enter targeting. */
function onCardClick(store: Store, run: RunState, handIndex: number): void {
  const combat = run.combat!;
  const card = combat.hand[handIndex];
  if (!card) return;
  const def = getCardDef(card.defId);
  if (def.unplayable) return;
  if (def.cost > combat.cycles) return;

  if (def.target === "enemy") {
    // Toggle selection for targeting (pure UI state).
    ui.selectedHandIndex =
      ui.selectedHandIndex === handIndex ? null : handIndex;
    store.requestRender();
  } else {
    ui.selectedHandIndex = null;
    store.playCard(handIndex);
  }
}

/**
 * Convert the last combat events into transient floating numbers anchored to
 * the relevant actor. Best-effort + presentation-only; never blocks gameplay.
 */
function queueFloaters(screen: HTMLElement, events: CombatEvent[]): void {
  if (!events || events.length === 0) return;
  // Defer until mounted so we can measure positions.
  requestAnimationFrame(() => {
    const root = screen;
    if (!root.isConnected) return;
    for (const ev of events) {
      if (ev.t === "damage" && ev.amount > 0) {
        const anchor =
          ev.targetUid === -1
            ? root.querySelector(".runner__avatar")
            : root.querySelector(`.ice[data-uid="${ev.targetUid}"] .ice__sprite`);
        spawnFloater(root, anchor, `-${ev.amount}`, "dmg");
      } else if (ev.t === "block" && ev.amount > 0 && ev.targetUid === -1) {
        const anchor = root.querySelector(".runner__avatar");
        spawnFloater(root, anchor, `▣${ev.amount}`, "block");
      }
    }
  });
}

function spawnFloater(
  root: HTMLElement,
  anchor: Element | null,
  text: string,
  cls: string,
): void {
  if (!anchor) return;
  const rootRect = root.getBoundingClientRect();
  const aRect = anchor.getBoundingClientRect();
  const f = div(`float ${cls}`);
  f.textContent = text;
  f.style.left = `${aRect.left - rootRect.left + aRect.width / 2 - 8}px`;
  f.style.top = `${aRect.top - rootRect.top}px`;
  root.appendChild(f);
  setTimeout(() => f.remove(), 950);
}

/** Keyboard handler for combat (wired by the app). Returns true if handled. */
export function combatKey(store: Store, key: string): boolean {
  const run = store.run;
  if (!run || !run.combat || run.phase !== "combat") return false;
  const combat = run.combat;

  // Number keys play that card (1..9, 0 = 10th).
  if (/^[0-9]$/.test(key)) {
    const idx = key === "0" ? 9 : Number(key) - 1;
    if (idx < combat.hand.length) {
      onCardClick(store, run, idx);
      return true;
    }
  }
  const k = key.toLowerCase();
  if (k === "e") {
    resetCombatUi();
    store.endTurn();
    return true;
  }
  if (k === "v") {
    store.open("deck");
    return true;
  }
  if (key === "ArrowRight") {
    ui.focusHand = Math.min(combat.hand.length - 1, ui.focusHand + 1);
    if (ui.focusHand < 0) ui.focusHand = 0;
    store.requestRender();
    return true;
  }
  if (key === "ArrowLeft") {
    ui.focusHand = Math.max(0, ui.focusHand - 1);
    store.requestRender();
    return true;
  }
  if (key === "Enter" && ui.focusHand >= 0 && ui.focusHand < combat.hand.length) {
    onCardClick(store, run, ui.focusHand);
    return true;
  }
  if (key === "Tab") {
    // Cycle target: if a card is selected & needs a target, play vs first living.
    if (ui.selectedHandIndex != null) {
      const living = combat.enemies.filter((e) => e.alive);
      if (living.length > 0) {
        const idx = ui.selectedHandIndex;
        ui.selectedHandIndex = null;
        store.playCard(idx, living[0]!.uid);
        return true;
      }
    }
  }
  if (key === "Escape") {
    if (ui.selectedHandIndex != null) {
      ui.selectedHandIndex = null;
      store.requestRender();
      return true;
    }
  }
  return false;
}

export { ui as combatUiState };
